import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, usersAPI } from '../services/api';
import Button from '../components/UI/Button';
import { Lock, Save, AlertCircle, CheckCircle, KeyRound, CreditCard, ChevronRight } from 'lucide-react';

const PLAN_LABELS = { free: 'Starter', standard: 'Standard', pro: 'Pro' };

const ProfilePage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    passwordCurrent: '',
    password: '',
    passwordConfirm: '',
  });

  // Success/error messages
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  // Check if user needs to set password (OAuth user without password)
  const needsToSetPassword = user?.hasPassword === false;

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data) => usersAPI.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['user']);
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setProfileMessage({ type: '', text: '' }), 3000);
    },
    onError: (error) => {
      setProfileMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update profile'
      });
      setTimeout(() => setProfileMessage({ type: '', text: '' }), 5000);
    },
  });

  // Update password mutation (for users who have a password)
  const updatePasswordMutation = useMutation({
    mutationFn: (data) => authAPI.updatePassword(data.passwordCurrent, data.password, data.passwordConfirm),
    onSuccess: () => {
      setPasswordData({ passwordCurrent: '', password: '', passwordConfirm: '' });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setTimeout(() => setPasswordMessage({ type: '', text: '' }), 3000);
    },
    onError: (error) => {
      setPasswordMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password'
      });
      setTimeout(() => setPasswordMessage({ type: '', text: '' }), 5000);
    },
  });

  // Set password mutation (for OAuth users setting password for first time)
  const setPasswordMutation = useMutation({
    mutationFn: (data) => authAPI.setPassword(data.password, data.passwordConfirm),
    onSuccess: () => {
      setPasswordData({ passwordCurrent: '', password: '', passwordConfirm: '' });
      setPasswordMessage({ type: 'success', text: 'Password set successfully! You can now login with email and password.' });
      queryClient.invalidateQueries(['user']);
      setTimeout(() => setPasswordMessage({ type: '', text: '' }), 5000);
    },
    onError: (error) => {
      setPasswordMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to set password'
      });
      setTimeout(() => setPasswordMessage({ type: '', text: '' }), 5000);
    },
  });

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileData);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.password !== passwordData.passwordConfirm) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match' });
      setTimeout(() => setPasswordMessage({ type: '', text: '' }), 3000);
      return;
    }

    if (needsToSetPassword) {
      setPasswordMutation.mutate(passwordData);
    } else {
      updatePasswordMutation.mutate(passwordData);
    }
  };

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      {/* Profile Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-full bg-ag-green/15 border-2 border-ag-green/30 flex items-center justify-center flex-shrink-0">
          <span className="text-2xl font-bold text-ag-green">{userInitial}</span>
        </div>
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold text-white truncate">{user?.name || 'Your Account'}</h1>
          <p className="text-sm text-gray-400 truncate">{user?.email}</p>
          {user?.authProvider === 'google' && (
            <span className="inline-flex items-center mt-1 px-2 py-0.5 text-xs bg-blue-500/15 text-blue-400 rounded-full border border-blue-500/20">
              Google Account
            </span>
          )}
        </div>
      </div>

      {/* Subscription Card */}
      <div className="mb-6 rounded-xl bg-gradient-to-r from-ag-black-700 to-ag-black-600 border border-ag-black-400 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-ag-green/10 flex items-center justify-center">
              <CreditCard className="text-ag-green" size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold">{PLAN_LABELS[user?.plan] || 'Starter'} Plan</span>
                <span className="px-2 py-0.5 text-xs rounded-full bg-ag-green/10 text-ag-green border border-ag-green/20">
                  Active
                </span>
              </div>
              <p className="text-sm text-gray-400 mt-0.5">
                {user?.credits ?? 0} credits remaining
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/manage-subscription')}
            className="px-4 py-2 text-sm font-semibold bg-ag-green text-ag-black-800 rounded-lg hover:bg-ag-green/90 transition-colors flex items-center gap-2"
          >
            Manage Subscription
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Profile Information Section */}
      <div className="rounded-xl bg-ag-black-700 border border-ag-black-400 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-ag-black-400">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Profile Information</h2>
        </div>
        <form onSubmit={handleProfileSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1.5">Name</label>
            <input
              type="text"
              value={profileData.name}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              className="w-full px-4 py-2.5 bg-ag-black-600 border border-ag-black-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ag-green/50 focus:border-ag-green/50 transition-colors"
              placeholder="Enter your name"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1.5">Email</label>
            <div className="relative">
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className="w-full px-4 py-2.5 bg-ag-black-600 border border-ag-black-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ag-green/50 focus:border-ag-green/50 transition-colors"
                placeholder="Enter your email"
              />
            </div>
          </div>

          {profileMessage.text && (
            <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
              profileMessage.type === 'success'
                ? 'bg-ag-green/10 text-ag-green border border-ag-green/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {profileMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {profileMessage.text}
            </div>
          )}

          <div className="pt-1">
            <Button
              type="submit"
              loading={updateProfileMutation.isPending}
              className="bg-ag-green text-ag-black-800 hover:bg-ag-green/90"
            >
              <Save size={16} className="mr-2" />
              Save Changes
            </Button>
          </div>
        </form>
      </div>

      {/* Password Section */}
      <div className="rounded-xl bg-ag-black-700 border border-ag-black-400 overflow-hidden">
        <div className="px-5 py-4 border-b border-ag-black-400">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
            {needsToSetPassword ? 'Set Password' : 'Change Password'}
          </h2>
        </div>
        <div className="p-5">
          {needsToSetPassword && (
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-400">
                You signed up with Google. Set a password to also login with email and password.
              </p>
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {!needsToSetPassword && (
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Current Password</label>
                <input
                  type="password"
                  value={passwordData.passwordCurrent}
                  onChange={(e) => setPasswordData({ ...passwordData, passwordCurrent: e.target.value })}
                  className="w-full px-4 py-2.5 bg-ag-black-600 border border-ag-black-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ag-green/50 focus:border-ag-green/50 transition-colors"
                  placeholder="Enter current password"
                  required={!needsToSetPassword}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  {needsToSetPassword ? 'Password' : 'New Password'}
                </label>
                <input
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  className="w-full px-4 py-2.5 bg-ag-black-600 border border-ag-black-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ag-green/50 focus:border-ag-green/50 transition-colors"
                  placeholder={needsToSetPassword ? 'Enter password' : 'Enter new password'}
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  Confirm {needsToSetPassword ? 'Password' : 'New Password'}
                </label>
                <input
                  type="password"
                  value={passwordData.passwordConfirm}
                  onChange={(e) => setPasswordData({ ...passwordData, passwordConfirm: e.target.value })}
                  className="w-full px-4 py-2.5 bg-ag-black-600 border border-ag-black-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ag-green/50 focus:border-ag-green/50 transition-colors"
                  placeholder={needsToSetPassword ? 'Confirm password' : 'Confirm new password'}
                  required
                  minLength={8}
                />
              </div>
            </div>

            {passwordMessage.text && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                passwordMessage.type === 'success'
                  ? 'bg-ag-green/10 text-ag-green border border-ag-green/20'
                  : 'bg-red-500/10 text-red-400 border border-red-500/20'
              }`}>
                {passwordMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {passwordMessage.text}
              </div>
            )}

            <div className="pt-1">
              <Button
                type="submit"
                loading={updatePasswordMutation.isPending || setPasswordMutation.isPending}
                className="bg-ag-green text-ag-black-800 hover:bg-ag-green/90"
              >
                {needsToSetPassword ? (
                  <>
                    <KeyRound size={16} className="mr-2" />
                    Set Password
                  </>
                ) : (
                  <>
                    <Lock size={16} className="mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
